import type { StateCreator } from 'zustand'
import { apiService, JobRequest } from '@/service/api'
import type { JobRecord, JobRunRecord } from '@/types/job'
import type { loadingState } from './loadingSlice'
import type { authSessionState } from './authSessionSlice'
import { toast } from 'sonner'

export interface JobState {
  jobs: JobRecord[]
  jobRuns: JobRunRecord[]
  currentPage: number
  pageSize: number
  totalPages: number
  totalJobs: number
  fetchJobs: () => Promise<void>
  fetchJobRuns: (jobId: string) => Promise<void>
  createJob: (job: JobRequest) => Promise<void>
  setPage: (page: number) => Promise<void>
  setPageSize: (size: number) => Promise<void>
  privateFetchJobs: (pageSize: number, pageNumber: number) => Promise<void>
}

const messages = {
  success: {
    fetchJobs: 'Jobs fetched successfully',
    fetchJobRuns: 'Job runs fetched successfully',
    createJob: 'Job created successfully',
  },
  error: {
    fetchJobs: 'Error fetching jobs',
    fetchJobRuns: 'Error fetching job runs',
    createJob: 'Error creating job',
  },
}

export const jobSlice: StateCreator<
  JobState & loadingState & authSessionState,
  [],
  [],
  JobState
> = (set, get) => ({
  jobs: [],
  jobRuns: [],
  currentPage: 1,
  pageSize: 10,
  totalPages: 1,
  totalJobs: 0,

  /**
   * Internal function to fetch jobs with the current pagination.
   */
  privateFetchJobs: async (pageSize: number, pageNumber: number) => {
    const { setLoading } = get()

    try {
      setLoading(true)
      const data = await apiService.getJobs(pageSize, pageNumber)
      set({
        jobs: data.results,
        totalPages: data.info.pages,
        totalJobs: data.info.count,
        pageSize: pageSize,
        currentPage: pageNumber,
      })
    } catch (e) {
      toast.error(messages.error.fetchJobs)
      set({ jobs: [] })
    } finally {
      setLoading(false)
    }
  },

  /**
   * Fetches the list of jobs with the pagination stored in the state.
   */
  fetchJobs: async () => {
    const { pageSize, currentPage, privateFetchJobs } = get()
    await privateFetchJobs(pageSize, currentPage)
  },

  /**
   * Changes the page and updates the list of jobs.
   */
  setPage: async (page: number) => {
    const { pageSize, privateFetchJobs } = get()
    await privateFetchJobs(pageSize, page)
  },

  /**
   * Changes the page size and resets to the first page.
   */
  setPageSize: async (size: number) => {
    const { privateFetchJobs } = get()
    await privateFetchJobs(size, 1)
  },

  fetchJobRuns: async (jobId: string) => {
    const { setLoading } = get()

    try {
      setLoading(true)
      const data = await apiService.getJobRuns(jobId)
      console.log(data)
      set({ jobRuns: data.results })
    } catch (e) {
      toast.error(messages.error.fetchJobRuns)
      set({ jobRuns: [] })
    } finally {
      setLoading(false)
    }
  },

  createJob: async (job: JobRequest) => {
    const { setLoading, fetchJobs } = get()

    console.log(job)
    try {
      setLoading(true)
      await apiService.createJob(job)
      await fetchJobs()
      toast.success(messages.success.createJob)
    } catch (e) {
      toast.error(messages.error.createJob)
    } finally {
      setLoading(false)
    }
  },
})
